/**
 * ROICalculator — Multi-tab savings calculator
 *
 * Tab 1: Automation ROI   – Staff time savings from automating a manual process
 * Tab 2: Downtime Risk    – Annual revenue at risk from unmonitored systems
 * Tab 3: Tech Bill Audit  – Conservative 20% savings on current IT/telecom spend
 *
 * Each tab shows a quantified result + a CTA that links to /assessments
 * with the calculated value pre-filled as a URL param.
 */
import { trackCtaClick } from "#utils/utils";
import {
  Badge,
  Box,
  Button,
  HStack,
  Heading,
  Icon,
  Link,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { FaBolt, FaChartLine, FaFileInvoiceDollar } from "react-icons/fa/index.js";

const MotionBox = motion(Box);

// ─── Tab 1: Automation ROI ────────────────────────────────────────────────────
const AutomationTab = () => {
  const [staffCount, setStaffCount] = useState(5);
  const [hoursPerWeek, setHoursPerWeek] = useState(8);
  const [hourlyWage, setHourlyWage] = useState(35);

  // Conservative: automation recaptures 70% of manual time
  const annualHours = staffCount * hoursPerWeek * 52;
  const annualCost = annualHours * hourlyWage;
  const savedHours = Math.round(annualHours * 0.7);
  const savedDollars = Math.round(annualCost * 0.7);

  const resultBg = useColorModeValue("green.50", "green.900");
  const resultBorder = useColorModeValue("green.100", "green.800");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const statColor = useColorModeValue("green.500", "green.300");

  const sliderProps = {
    trackH: "3" as const,
    thumbBorder: "2px solid",
  };

  return (
    <VStack spacing={5} align="stretch">
      {/* Staff headcount */}
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Staff involved in this process</Text>
          <Badge colorScheme="blue" fontSize="sm" px={2}>{staffCount} people</Badge>
        </HStack>
        <Slider min={1} max={50} step={1} value={staffCount} onChange={setStaffCount}>
          <SliderTrack bg="gray.200" h={sliderProps.trackH}><SliderFilledTrack bg="blue.500" /></SliderTrack>
          <SliderThumb boxSize={5} bg="white" border={sliderProps.thumbBorder} borderColor="blue.500" />
        </Slider>
      </Box>

      {/* Hours per week */}
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Hours per person per week on this task</Text>
          <Badge colorScheme="blue" fontSize="sm" px={2}>{hoursPerWeek} hrs</Badge>
        </HStack>
        <Slider min={1} max={40} step={1} value={hoursPerWeek} onChange={setHoursPerWeek}>
          <SliderTrack bg="gray.200" h={sliderProps.trackH}><SliderFilledTrack bg="blue.500" /></SliderTrack>
          <SliderThumb boxSize={5} bg="white" border={sliderProps.thumbBorder} borderColor="blue.500" />
        </Slider>
      </Box>

      {/* Hourly wage */}
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Average hourly wage (fully loaded)</Text>
          <Badge colorScheme="blue" fontSize="sm" px={2}>${hourlyWage}/hr</Badge>
        </HStack>
        <Slider min={15} max={150} step={5} value={hourlyWage} onChange={setHourlyWage}>
          <SliderTrack bg="gray.200" h={sliderProps.trackH}><SliderFilledTrack bg="blue.500" /></SliderTrack>
          <SliderThumb boxSize={5} bg="white" border={sliderProps.thumbBorder} borderColor="blue.500" />
        </Slider>
      </Box>

      {/* Result */}
      <MotionBox
        key={savedDollars}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 } as any}
        bg={resultBg}
        border="1px solid"
        borderColor={resultBorder}
        borderRadius="xl"
        p={4}
      >
        <HStack justify="space-around" flexWrap="wrap" spacing={3}>
          <Stat textAlign="center">
            <StatLabel fontSize="xs" color={labelColor}>Hours Recaptured / Year</StatLabel>
            <StatNumber fontSize="2xl" color={statColor} fontWeight="black">
              {savedHours.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs" color={labelColor}>70% automation efficiency</StatHelpText>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs" color={labelColor}>Annual Labor Cost Saved</StatLabel>
            <StatNumber fontSize="2xl" color={statColor} fontWeight="black">
              ${savedDollars.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs" color={labelColor}>Conservative estimate</StatHelpText>
          </Stat>
        </HStack>
      </MotionBox>

      <Button
        as={Link}
        href={`/assessments?calculator=automation&savings=${savedDollars}`}
        colorScheme="green"
        size="md"
        w="full"
        fontWeight="bold"
        _hover={{ textDecoration: "none", transform: "translateY(-2px)", boxShadow: "lg" }}
        transition="all 0.2s"
        onClick={() =>
          trackCtaClick({
            cta_label: "Get My Automation Assessment",
            cta_location: "ROICalculator – Automation",
            destination_url: `/assessments?calculator=automation&savings=${savedDollars}`,
          })
        }
      >
        Get My Automation Assessment
      </Button>
    </VStack>
  );
};

// ─── Tab 2: Downtime Risk ─────────────────────────────────────────────────────
const DowntimeTab = () => {
  const [hourlyRevenue, setHourlyRevenue] = useState(5000);
  const [downtimeHours, setDowntimeHours] = useState(4);

  const annualRisk = hourlyRevenue * downtimeHours * 12;
  const perIncident = hourlyRevenue * downtimeHours;

  const resultBg = useColorModeValue("red.50", "red.900");
  const resultBorder = useColorModeValue("red.100", "red.800");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const statColor = useColorModeValue("red.500", "red.300");

  return (
    <VStack spacing={5} align="stretch">
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Estimated revenue per hour (business hours)</Text>
          <Badge colorScheme="red" fontSize="sm" px={2}>${hourlyRevenue.toLocaleString()}/hr</Badge>
        </HStack>
        <Slider min={500} max={100000} step={500} value={hourlyRevenue} onChange={setHourlyRevenue}>
          <SliderTrack bg="gray.200" h="3"><SliderFilledTrack bg="red.500" /></SliderTrack>
          <SliderThumb boxSize={5} bg="white" border="2px solid" borderColor="red.500" />
        </Slider>
      </Box>

      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Average downtime hours per incident</Text>
          <Badge colorScheme="red" fontSize="sm" px={2}>{downtimeHours} hrs</Badge>
        </HStack>
        <Slider min={1} max={72} step={1} value={downtimeHours} onChange={setDowntimeHours}>
          <SliderTrack bg="gray.200" h="3"><SliderFilledTrack bg="red.500" /></SliderTrack>
          <SliderThumb boxSize={5} bg="white" border="2px solid" borderColor="red.500" />
        </Slider>
      </Box>

      <MotionBox
        key={annualRisk}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 } as any}
        bg={resultBg}
        border="1px solid"
        borderColor={resultBorder}
        borderRadius="xl"
        p={4}
      >
        <HStack justify="space-around" flexWrap="wrap" spacing={3}>
          <Stat textAlign="center">
            <StatLabel fontSize="xs" color={labelColor}>Cost Per Incident</StatLabel>
            <StatNumber fontSize="2xl" color={statColor} fontWeight="black">
              ${perIncident.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs" color={labelColor}>Direct revenue exposure</StatHelpText>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs" color={labelColor}>Annual Risk Exposure</StatLabel>
            <StatNumber fontSize="2xl" color={statColor} fontWeight="black">
              ${annualRisk.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs" color={labelColor}>Assuming 1 incident/month</StatHelpText>
          </Stat>
        </HStack>
      </MotionBox>

      <Button
        as={Link}
        href={`/assessments?calculator=downtime&risk=${annualRisk}`}
        colorScheme="red"
        size="md"
        w="full"
        fontWeight="bold"
        _hover={{ textDecoration: "none", transform: "translateY(-2px)", boxShadow: "lg" }}
        transition="all 0.2s"
        onClick={() =>
          trackCtaClick({
            cta_label: "Get My Security Assessment",
            cta_location: "ROICalculator – Downtime",
            destination_url: `/assessments?calculator=downtime&risk=${annualRisk}`,
          })
        }
      >
        Get My Free Security Assessment
      </Button>
    </VStack>
  );
};

// ─── Tab 3: Tech Bill Audit ───────────────────────────────────────────────────
const TechBillTab = () => {
  const [currentSpend, setCurrentSpend] = useState(2500);
  const estimatedSavings = Math.round(currentSpend * 0.2);
  const yearlySavings = estimatedSavings * 12;

  const resultBg = useColorModeValue("blue.50", "blue.900");
  const resultBorder = useColorModeValue("blue.100", "blue.800");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const statColor = useColorModeValue("green.500", "green.300");

  return (
    <VStack spacing={5} align="stretch">
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Monthly IT / Telecom spend</Text>
          <Badge colorScheme="blue" fontSize="sm" px={2}>${currentSpend.toLocaleString()}/mo</Badge>
        </HStack>
        <Slider min={500} max={20000} step={100} value={currentSpend} onChange={setCurrentSpend}>
          <SliderTrack bg="gray.200" h="3"><SliderFilledTrack bg="blue.500" /></SliderTrack>
          <SliderThumb boxSize={5} bg="white" border="2px solid" borderColor="blue.500" />
        </Slider>
      </Box>

      <MotionBox
        key={yearlySavings}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 } as any}
        bg={resultBg}
        border="1px solid"
        borderColor={resultBorder}
        borderRadius="xl"
        p={4}
      >
        <HStack justify="space-around" flexWrap="wrap" spacing={3}>
          <Stat textAlign="center">
            <StatLabel fontSize="xs" color={labelColor}>Est. Monthly Savings</StatLabel>
            <StatNumber fontSize="2xl" color={statColor} fontWeight="black">
              ${estimatedSavings.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs" color={labelColor}>Conservative 20% benchmark</StatHelpText>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs" color={labelColor}>Annual Savings Potential</StatLabel>
            <StatNumber fontSize="2xl" color={statColor} fontWeight="black">
              ${yearlySavings.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs" color={labelColor}>Based on 200+ vendor contracts</StatHelpText>
          </Stat>
        </HStack>
      </MotionBox>

      <Button
        as={Link}
        href={`/assessments?calculator=bill&savings=${yearlySavings}`}
        colorScheme="green"
        size="md"
        w="full"
        fontWeight="bold"
        _hover={{ textDecoration: "none", transform: "translateY(-2px)", boxShadow: "lg" }}
        transition="all 0.2s"
        onClick={() =>
          trackCtaClick({
            cta_label: "Analyze My Tech Bills",
            cta_location: "ROICalculator – TechBill",
            destination_url: `/assessments?calculator=bill&savings=${yearlySavings}`,
          })
        }
      >
        Analyze My Tech Bills
      </Button>
    </VStack>
  );
};

// ─── Root export ──────────────────────────────────────────────────────────────
export const ROICalculator: React.FC = () => {
  const bgBox = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const tabSelectedBg = useColorModeValue("blue.50", "gray.700");
  const tabSelectedColor = useColorModeValue("blue.600", "blue.200");

  return (
    <Box
      bg={bgBox}
      borderRadius="2xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      w="full"
      mx="auto"
    >
      <Tabs isFitted colorScheme="blue">
        <TabList bg={useColorModeValue("gray.50", "gray.900")} borderBottom="1px solid" borderColor={borderColor}>
          <Tab
            py={4}
            fontSize="sm"
            fontWeight="bold"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg, borderBottomColor: "blue.500" }}
            gap={2}
          >
            <Icon as={FaBolt} />
            Automation ROI
          </Tab>
          <Tab
            py={4}
            fontSize="sm"
            fontWeight="bold"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg, borderBottomColor: "red.400" }}
            gap={2}
          >
            <Icon as={FaChartLine} />
            Downtime Risk
          </Tab>
          <Tab
            py={4}
            fontSize="sm"
            fontWeight="bold"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg, borderBottomColor: "green.500" }}
            gap={2}
          >
            <Icon as={FaFileInvoiceDollar} />
            Bill Audit
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={6}>
            <Heading size="sm" mb={1}>Automation Savings Estimator</Heading>
            <Text fontSize="xs" color={useColorModeValue("gray.500", "gray.400")} mb={5}>
              See how much your team could recover by automating one manual process.
            </Text>
            <AutomationTab />
          </TabPanel>
          <TabPanel p={6}>
            <Heading size="sm" mb={1}>Downtime Revenue Risk</Heading>
            <Text fontSize="xs" color={useColorModeValue("gray.500", "gray.400")} mb={5}>
              Calculate what unmonitored outages are costing your business annually.
            </Text>
            <DowntimeTab />
          </TabPanel>
          <TabPanel p={6}>
            <Heading size="sm" mb={1}>Technology Bill Audit</Heading>
            <Text fontSize="xs" color={useColorModeValue("gray.500", "gray.400")} mb={5}>
              Estimate savings from benchmarking your telecom and IT spend against 200+ vendors.
            </Text>
            <TechBillTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
